(() => {
  const roots = document.querySelectorAll('[data-zaoud-notes-directory]');
  if (!roots.length) return;

  const PLACEHOLDER_SVG =
    '<svg class="placeholder-svg zaoud-notes-directory__card-placeholder" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5" aria-hidden="true"><path d="M324.5 212.7H201c-6.5 0-11.8 5.3-11.8 11.8v176.0c0 6.5 5.3 11.8 11.8 11.8h123.5c6.5 0 11.8-5.3 11.8-11.8V224.5c0-6.5-5.3-11.8-11.8-11.8zm0 187.8H201V224.5h123.5v176z"/><circle cx="245.2" cy="265.1" r="19.5"/><path d="M201 365.5l41.1-41.1 28.3 28.3 55.1-55.1L324.5 347"/></svg>';

  const escapeHtml = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const findGroup = (root, groupKey) =>
    [...root.querySelectorAll('[data-zaoud-notes-group]')].find(
      (group) => group.dataset.group === groupKey
    );

  const ensureGroup = (root, groupKey) => {
    let group = findGroup(root, groupKey);
    if (group) return group;

    const groups = root.querySelector('.zaoud-notes-directory__groups');
    if (!groups) return null;

    group = document.createElement('section');
    group.className = 'zaoud-notes-directory__group';
    group.dataset.zaoudNotesGroup = '';
    group.dataset.group = groupKey;
    group.innerHTML = `
      <div class="zaoud-notes-directory__sticky">
        <div class="zaoud-notes-directory__banner zaoud-notes-directory__banner--fallback">
          <span class="zaoud-notes-directory__banner-scrim" aria-hidden="true"></span>
          <div class="zaoud-notes-directory__banner-content">
            <h2 class="zaoud-notes-directory__group-heading">${escapeHtml(groupKey)}</h2>
          </div>
        </div>
      </div>
      <ul class="zaoud-notes-directory__grid" role="list"></ul>
    `;
    groups.appendChild(group);
    return group;
  };

  const renderCard = (note, imageSize) => {
    const title = (note.title || '').trim();
    if (!title) return null;

    const link = (note.link || '#').trim() || '#';
    const image = note.image || '';
    const imageWidth = Math.max(1, Number(imageSize) || 75) * 2;
    const external = link.includes('http');

    const li = document.createElement('li');
    li.className = 'zaoud-notes-directory__card';
    li.dataset.zaoudNoteCard = '';
    li.dataset.title = title.toLowerCase();

    const mediaClass = image
      ? 'zaoud-notes-directory__card-media'
      : 'zaoud-notes-directory__card-media zaoud-notes-directory__card-media--placeholder';

    const imageAlt = `${title} fragrance note`;
    const mediaHtml = image
      ? `<img
          src="${escapeHtml(image)}"
          alt="${escapeHtml(imageAlt)}"
          width="${imageWidth}"
          height="${imageWidth}"
          loading="lazy"
          class="zaoud-notes-directory__card-image"
        >`
      : `<span class="zaoud-notes-directory__card-placeholder-wrap" role="img" aria-label="${escapeHtml(imageAlt)}">${PLACEHOLDER_SVG}</span>`;

    li.innerHTML = `
      <a
        class="zaoud-notes-directory__card-link"
        href="${escapeHtml(link)}"
        ${external ? 'target="_blank" rel="noopener"' : ''}
      >
        <span class="${mediaClass}">${mediaHtml}</span>
        <span class="zaoud-notes-directory__card-title">${escapeHtml(title)}</span>
      </a>
    `;
    return li;
  };

  const noteKey = (noteOrCard) => {
    if (noteOrCard instanceof HTMLElement) {
      const title = noteOrCard.dataset.title || '';
      const href = noteOrCard.querySelector('a')?.getAttribute('href') || '';
      return `${title}|${href}`;
    }
    return `${String(noteOrCard.title || '')
      .trim()
      .toLowerCase()}|${String(noteOrCard.link || '').trim()}`;
  };

  const hideEmptyGroups = (root) => {
    root.querySelectorAll('[data-zaoud-notes-group]').forEach((group) => {
      const hasCards = group.querySelector('[data-zaoud-note-card]');
      group.hidden = !hasCards;
    });
  };

  const appendNotes = (root, notes, imageSize, seen) => {
    let added = 0;
    (notes || []).forEach((note) => {
      const key = noteKey(note);
      if (!key || seen.has(key)) return;
      seen.add(key);

      const groupKey = (note.group || 'UNCATEGORIZED').trim() || 'UNCATEGORIZED';
      const group = ensureGroup(root, groupKey);
      const grid = group?.querySelector('.zaoud-notes-directory__grid');
      const card = renderCard(note, imageSize);
      if (!grid || !card) return;
      grid.appendChild(card);
      added += 1;
    });
    return added;
  };

  const buildDataUrl = (page, pageParam) => {
    const url = new URL(window.location.pathname, window.location.origin);
    const current = new URL(window.location.href);
    current.searchParams.forEach((value, key) => {
      if (key === 'view' || key === pageParam) return;
      url.searchParams.set(key, value);
    });
    url.searchParams.set('view', 'notes-data');
    url.searchParams.set(pageParam || 'page', String(page));
    return url.toString();
  };

  const fetchNotesPage = async (page, pageParam) => {
    // Do NOT send Accept: application/json — Shopify then returns the Page
    // resource API ({"page":{...}}) instead of templates/page.notes-data.liquid.
    const response = await fetch(buildDataUrl(page, pageParam));
    if (!response.ok) {
      throw new Error(`Notes data page ${page} failed: ${response.status}`);
    }
    const data = JSON.parse(await response.text());
    if (!data || !Array.isArray(data.notes)) {
      throw new Error(
        `Notes data page ${page} returned unexpected JSON (missing notes array)`
      );
    }
    return data;
  };

  const loadRemainingPages = async (root) => {
    let totalPages = Number(root.dataset.notesPages || 1);
    const currentPage = Number(root.dataset.notesPage || 1);
    let pageParam = root.dataset.notesPageParam || 'page';
    const imageSize = Number(root.dataset.notesImageSize || 75);

    const seen = new Set(
      [...root.querySelectorAll('[data-zaoud-note-card]')].map((card) => noteKey(card))
    );

    if (totalPages <= 1) {
      hideEmptyGroups(root);
      root.removeAttribute('aria-busy');
      return;
    }

    // Bootstrap from the JSON feed so we use its metaobject page_param (not plain ?page=).
    const bootstrap = await fetchNotesPage(1, pageParam);
    if (bootstrap.page_param) pageParam = bootstrap.page_param;
    if (bootstrap.pages) totalPages = Number(bootstrap.pages) || totalPages;
    appendNotes(root, bootstrap.notes, imageSize, seen);

    const pages = [];
    for (let page = 2; page <= totalPages; page += 1) {
      if (page === currentPage) continue;
      pages.push(page);
    }

    const results = await Promise.all(pages.map((page) => fetchNotesPage(page, pageParam)));
    results.forEach((data) => {
      appendNotes(root, data.notes, imageSize, seen);
    });

    hideEmptyGroups(root);
    root.removeAttribute('aria-busy');
  };

  roots.forEach((root) => {
    const input = root.querySelector('[data-zaoud-notes-search]');
    const empty = root.querySelector('[data-zaoud-notes-empty]');
    if (!input) return;

    const filter = () => {
      const query = (input.value || '').trim().toLowerCase();
      const groups = [...root.querySelectorAll('[data-zaoud-notes-group]')];
      let visibleTotal = 0;

      groups.forEach((group) => {
        const cards = [...group.querySelectorAll('[data-zaoud-note-card]')];
        let visibleInGroup = 0;

        cards.forEach((card) => {
          const title = card.dataset.title || '';
          const match = !query || title.includes(query);
          card.hidden = !match;
          if (match) visibleInGroup += 1;
        });

        group.hidden = visibleInGroup === 0;
        visibleTotal += visibleInGroup;
      });

      if (empty) {
        empty.hidden = visibleTotal > 0;
      }
    };

    input.addEventListener('input', filter);
    input.addEventListener('search', filter);

    loadRemainingPages(root)
      .then(() => filter())
      .catch((error) => {
        console.error('[zaoud-notes-directory]', error);
        root.removeAttribute('aria-busy');
        hideEmptyGroups(root);
        filter();
      });
  });
})();
